import { NavLink, useNavigate } from 'react-router-dom';
import {
  Package,
  Users,
  Home,
  Settings,
  UserCheck,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ModuleDefinition, ModuleSidebarConfig } from '../modules/ModuleContext';
import { useAuth } from '../auth/AuthContext';
import { sidebarIconMap } from './sidebarIconMap';

// Icon mapping — use shared sidebarIconMap
const iconMap = sidebarIconMap;

interface SidebarProps {
  currentModule: ModuleDefinition | null;
  sidebarConfig: ModuleSidebarConfig | null;
}

export function Sidebar({ currentModule, sidebarConfig }: SidebarProps) {
  const navigate = useNavigate();
  const { account } = useAuth();
  const { t } = useTranslation('layout');

  const getIcon = (iconName: string) => {
    const Icon = iconMap[iconName];
    return Icon ? <Icon size={18} /> : null;
  };

  return (
    <aside className="w-60 h-full bg-slate-200 dark:bg-gray-800 text-gray-700 dark:text-white flex-shrink-0 flex flex-col border-r border-gray-300 dark:border-gray-700">
      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto">
        {/* Module Header */}
        {currentModule && (
          <div
            className="px-4 py-4 border-b border-gray-300 dark:border-gray-700"
            style={{ borderLeftColor: currentModule.color, borderLeftWidth: '3px' }}
          >
            <h2 className="font-semibold">{t('modules.' + currentModule.id, { defaultValue: currentModule.name })}</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t(currentModule.description)}</p>
          </div>
        )}

        {/* Navigation */}
        <nav className="p-2">
        {/* If we have a module sidebar config, show it */}
        {sidebarConfig ? (
          sidebarConfig.sections.map((section, sectionIdx) => (
            <div key={sectionIdx} className="mb-4 last:mb-0">
              {section.title && (
                <h3 className="px-3 py-2 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                  {t(section.title)}
                </h3>
              )}
              <ul className="space-y-1">
                {section.items.map((item) => {
                  // Add 'end' prop for module base paths and for items that are prefixes of other items
                  const isModuleBasePath = currentModule && item.href === currentModule.basePath;
                  const allItems = sidebarConfig.sections.flatMap(s => s.items);
                  const isParentOfOther = allItems.some(other => other.href !== item.href && other.href.startsWith(item.href + '/'));
                  const useEnd = isModuleBasePath || isParentOfOther;

                  // Generate data-tour attribute from href
                  const getTourId = (href: string): string => {
                    const parts = href.split('/');
                    if (parts.length === 2) return 'sidebar-dashboard'; // e.g., /stock
                    return `sidebar-${parts.slice(2).join('-')}`; // e.g., /stock/products → sidebar-products
                  };

                  return (
                    <li key={item.href}>
                      <NavLink
                        to={item.href}
                        end={useEnd || undefined}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                            isActive
                              ? 'bg-slate-300 dark:bg-white/15 text-gray-900 dark:text-white font-medium'
                              : 'text-gray-600 dark:text-gray-300 hover:bg-slate-300/70 dark:hover:bg-white/10 hover:text-gray-900 dark:hover:text-white'
                          }`
                        }
                        data-tour={getTourId(item.href)}
                      >
                        {getIcon(item.icon)}
                        <span className="whitespace-nowrap">{t(item.label)}</span>
                        {item.badge !== undefined && item.badge > 0 && (
                          <span className="ml-auto bg-accent-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                            {item.badge}
                          </span>
                        )}
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))
        ) : (
          // Homepage - show quick access to all modules
          <>
            <div className="mb-4">
              <h3 className="px-3 py-2 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                Navigation
              </h3>
              <ul className="space-y-1">
                <li>
                  <NavLink
                    to="/"
                    end
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                        isActive
                          ? 'bg-slate-300 dark:bg-white/15 text-gray-900 dark:text-white font-medium'
                          : 'text-gray-600 dark:text-gray-300 hover:bg-slate-300/70 dark:hover:bg-white/10 hover:text-gray-900 dark:hover:text-white'
                      }`
                    }
                  >
                    <Home size={18} />
                    <span>Accueil</span>
                  </NavLink>
                </li>
              </ul>
            </div>

            <div className="mb-4">
              <h3 className="px-3 py-2 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                Modules
              </h3>
              <ul className="space-y-1">
                <li>
                  <NavLink
                    to="/stock"
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                        isActive
                          ? 'bg-slate-300 dark:bg-white/15 text-gray-900 dark:text-white font-medium'
                          : 'text-gray-600 dark:text-gray-300 hover:bg-slate-300/70 dark:hover:bg-white/10 hover:text-gray-900 dark:hover:text-white'
                      }`
                    }
                  >
                    <Package size={18} />
                    <span>Stock</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/zilloteam"
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                        isActive
                          ? 'bg-slate-300 dark:bg-white/15 text-gray-900 dark:text-white font-medium'
                          : 'text-gray-600 dark:text-gray-300 hover:bg-slate-300/70 dark:hover:bg-white/10 hover:text-gray-900 dark:hover:text-white'
                      }`
                    }
                  >
                    <Users size={18} />
                    <span>Zillo</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/equipe"
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                        isActive
                          ? 'bg-slate-300 dark:bg-white/15 text-gray-900 dark:text-white font-medium'
                          : 'text-gray-600 dark:text-gray-300 hover:bg-slate-300/70 dark:hover:bg-white/10 hover:text-gray-900 dark:hover:text-white'
                      }`
                    }
                  >
                    <UserCheck size={18} />
                    <span>Équipe</span>
                  </NavLink>
                </li>
              </ul>
            </div>
          </>
        )}

      </nav>
      </div>

      {/* Paramètres button — sticky above footer */}
      {(currentModule?.id === 'stock' || currentModule?.id === 'repondeur' || currentModule?.id === 'equipe' || currentModule?.id === 'zillodocs' || currentModule?.id === 'zillotv') && (
        <div className="px-4 py-1.5 border-t border-gray-300 dark:border-gray-700 flex-shrink-0">
          <NavLink
            to={currentModule.id === 'zillotv' ? '/zillo-tv/settings' : `/${currentModule.id}/settings`}
            data-tour="sidebar-settings"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                isActive
                  ? 'bg-slate-300 dark:bg-white/15 text-gray-900 dark:text-white font-medium'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-slate-300/70 dark:hover:bg-white/10 hover:text-gray-900 dark:hover:text-white'
              }`
            }
          >
            <Settings size={18} />
            <span>{t('sidebar.items.settings')}</span>
          </NavLink>
        </div>
      )}

      {/* Footer — sticky at bottom of sidebar */}
      <div className="px-4 py-3 border-t border-gray-300 dark:border-gray-700 flex-shrink-0">
        <p className="text-xs text-gray-400 dark:text-gray-500 text-center">
          {account?.name}
        </p>
      </div>
    </aside>
  );
}

export default Sidebar;
